/**
 * @file api-admin-users.ts
 * @description Обёртки над `/admin/users*` (phase04 шаг 6).
 *
 * Типы — зеркало AdminUserDto / AdminUserContextDto из
 * apps/api/src/users/admin-users.service.ts. Контракт скоупа на бэке:
 * платформенный админ видит всех; владелец магазина — только тех, у кого
 * есть контекст в его магазине, и только эти контексты в выдаче.
 */

import { apiFetch } from "./api-client";

export interface AdminUserContext {
  id: string;
  contextType: "PLATFORM" | "SHOP";
  shop: { id: string; slug: string; displayName: string } | null;
  role: {
    id: string;
    code: string;
    displayName: string;
    category: "PLATFORM" | "SHOP";
  };
}

export interface AdminUser {
  id: string;
  email: string;
  displayName: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  contexts: AdminUserContext[];
}

export interface CreateUserPayload {
  email: string;
  password: string;
  displayName?: string;
}

export interface UpdateUserPayload {
  displayName?: string;
  isActive?: boolean;
}

export interface AddContextPayload {
  contextType: "PLATFORM" | "SHOP";
  shopId?: string;
  roleId: string;
}

export function listAdminUsers(): Promise<AdminUser[]> {
  return apiFetch<AdminUser[]>("/admin/users");
}

/** Один пользователь с его кабинетами (`GET /admin/users/:id`). */
export function getAdminUser(userId: string): Promise<AdminUser> {
  return apiFetch<AdminUser>(`/admin/users/${encodeURIComponent(userId)}`);
}

export function createAdminUser(payload: CreateUserPayload): Promise<AdminUser> {
  return apiFetch<AdminUser>("/admin/users", { method: "POST", json: payload });
}

export function softDeleteAdminUser(userId: string): Promise<AdminUser> {
  return apiFetch<AdminUser>(`/admin/users/${encodeURIComponent(userId)}`, { method: "DELETE" });
}

export function reactivateAdminUser(userId: string): Promise<AdminUser> {
  return apiFetch<AdminUser>(`/admin/users/${encodeURIComponent(userId)}`, {
    method: "PATCH",
    json: { isActive: true },
  });
}

export function updateAdminUser(
  userId: string,
  payload: UpdateUserPayload,
): Promise<AdminUser> {
  return apiFetch<AdminUser>(`/admin/users/${encodeURIComponent(userId)}`, {
    method: "PATCH",
    json: payload,
  });
}

export function resetAdminUserPassword(
  userId: string,
  password: string,
): Promise<AdminUser> {
  return apiFetch<AdminUser>(`/admin/users/${encodeURIComponent(userId)}/password`, {
    method: "POST",
    json: { password },
  });
}

export function hardDeleteAdminUser(userId: string): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(
    `/admin/users/${encodeURIComponent(userId)}/permanent`,
    { method: "DELETE" },
  );
}

export function addAdminUserContext(
  userId: string,
  payload: AddContextPayload,
): Promise<AdminUser> {
  return apiFetch<AdminUser>(`/admin/users/${encodeURIComponent(userId)}/contexts`, {
    method: "POST",
    json: payload,
  });
}

export function removeAdminUserContext(userId: string, contextId: string): Promise<AdminUser> {
  return apiFetch<AdminUser>(
    `/admin/users/${encodeURIComponent(userId)}/contexts/${encodeURIComponent(contextId)}`,
    { method: "DELETE" },
  );
}
